import config from "../config";
import {
  CreditCardHolderInfo,
  tokenizeSubscriptionCreditCard,
} from "../services/subscriptionService";

export interface CreditCardTokenizeInput {
  number: string;
  holderName: string;
  expiry: string;
  ccv: string;
  holderInfo?: CreditCardHolderInfo;
}

export interface CreditCardTokenizeResult {
  creditCardToken: string;
  creditCardLast4: string;
}

declare global {
  interface Window {
    Asaas?: {
      encryptCreditCard?: (
        payload: Record<string, unknown>
      ) => Promise<{ creditCardToken?: string; creditCardNumber?: string }>;
    };
  }
}

let scriptLoadPromise: Promise<boolean> | null = null;

const parseExpiry = (
  value: string
): { expiryMonth: string; expiryYear: string } | null => {
  const normalized = value.replace(/\s/g, "");
  const match = normalized.match(/^(\d{2})\/?(\d{2}|\d{4})$/);

  if (!match) {
    return null;
  }

  const expiryMonth = match[1];
  let expiryYear = match[2];

  if (expiryYear.length === 2) {
    expiryYear = `20${expiryYear}`;
  }

  return { expiryMonth, expiryYear };
};

export const loadAsaasEncryptionScript = (): Promise<boolean> => {
  if (typeof window === "undefined") {
    return Promise.resolve(false);
  }

  if (window.Asaas?.encryptCreditCard) {
    return Promise.resolve(true);
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise<boolean>((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-adatis-asaas-encryption="true"]'
    );

    if (existing) {
      existing.addEventListener("load", () => resolve(Boolean(window.Asaas?.encryptCreditCard)));
      existing.addEventListener("error", () => resolve(false));
      return;
    }

    const script = document.createElement("script");
    script.src = config.asaas.encryptionScriptUrl;
    script.async = true;
    script.dataset.adatisAsaasEncryption = "true";
    script.onload = () => resolve(Boolean(window.Asaas?.encryptCreditCard));
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
};

const tokenizeWithClientScript = async (
  input: CreditCardTokenizeInput
): Promise<CreditCardTokenizeResult | null> => {
  const encrypt = window.Asaas?.encryptCreditCard;

  if (!encrypt) {
    return null;
  }

  const expiry = parseExpiry(input.expiry);

  if (!expiry) {
    throw new Error("INVALID_EXPIRY");
  }

  const digits = input.number.replace(/\D/g, "");
  const response = await encrypt({
    creditCard: {
      holderName: input.holderName.trim(),
      number: digits,
      expiryMonth: expiry.expiryMonth,
      expiryYear: expiry.expiryYear,
      ccv: input.ccv.replace(/\D/g, ""),
    },
    creditCardHolderInfo: input.holderInfo ?? {},
  });

  const token = response.creditCardToken?.trim();

  if (!token) {
    return null;
  }

  const last4 =
    response.creditCardNumber?.replace(/\D/g, "").slice(-4) || digits.slice(-4);

  return {
    creditCardToken: token,
    creditCardLast4: last4,
  };
};

const tokenizeWithBackend = async (
  input: CreditCardTokenizeInput
): Promise<CreditCardTokenizeResult> => {
  const expiry = parseExpiry(input.expiry);

  if (!expiry) {
    throw new Error("INVALID_EXPIRY");
  }

  const digits = input.number.replace(/\D/g, "");
  const response = await tokenizeSubscriptionCreditCard({
    credit_card: {
      number: digits,
      holder_name: input.holderName.trim(),
      expiry_month: expiry.expiryMonth,
      expiry_year: expiry.expiryYear,
      ccv: input.ccv.replace(/\D/g, ""),
    },
    credit_card_holder_info: input.holderInfo,
  });

  return {
    creditCardToken: response.data.credit_card_token,
    creditCardLast4: response.data.credit_card_last4,
  };
};

export const tokenizeCreditCard = async (
  input: CreditCardTokenizeInput
): Promise<CreditCardTokenizeResult> => {
  const scriptReady = await loadAsaasEncryptionScript();

  if (scriptReady) {
    const clientResult = await tokenizeWithClientScript(input);

    if (clientResult) {
      return clientResult;
    }
  }

  return tokenizeWithBackend(input);
};

export const extractCardLast4 = (value: string): string =>
  value.replace(/\D/g, "").slice(-4);
